import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSocket, useChat } from '../../hooks/useSocket';
import { useAuth } from '../../context/AuthContext';
import { ChatMessage as BaseChatMessage } from '../../types';

interface GroupChatProps {
  roomId: string | null;
  roomName?: string;
  className?: string;
  height?: string;
}

interface ChatMessage extends BaseChatMessage {
  reactions?: Array<{
    emoji: string;
    count: number;
    users: string[];
  }>;
}

const GroupChat: React.FC<GroupChatProps> = ({
  roomId,
  roomName = 'General',
  className = '',
  height = 'h-96'
}) => {
  const { user } = useAuth();
  const { isConnected } = useSocket();
  const {
    messages,
    typingUsers,
    sendMessage,
    setTyping,
    addReaction,
    removeReaction
  } = useChat(roomId);

  const [messageText, setMessageText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout>();

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Handle typing indicators
  const handleTyping = useCallback((text: string) => {
    setMessageText(text);
    
    if (text.length > 0 && !isTyping) {
      setIsTyping(true);
      setTyping(true);
    }
    
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set new timeout to stop typing
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      setTyping(false);
    }, 1500);
  }, [isTyping, setTyping]);

  // Send message handler
  const handleSendMessage = useCallback(() => {
    if (messageText.trim() && roomId && isConnected) {
      sendMessage(messageText.trim());
      setMessageText('');
      setIsTyping(false);
      setTyping(false);
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Focus back to input
      inputRef.current?.focus();
    }
  }, [messageText, roomId, isConnected, sendMessage, setTyping]);

  // Handle Enter key
  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  // Format timestamp
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  // Group messages by sender and time proximity
  const groupMessages = (messages: ChatMessage[]) => {
    const groups: Array<{
      userId: string;
      username: string;
      messages: ChatMessage[];
      timestamp: string;
    }> = [];

    messages.forEach((message, index) => {
      const prevMessage = messages[index - 1];
      const timeDiff = prevMessage ? 
        new Date(message.timestamp).getTime() - new Date(prevMessage.timestamp).getTime() : 
        0;

      // Group if same user and within 5 minutes
      if (prevMessage && 
          prevMessage.userId === message.userId && 
          timeDiff < 5 * 60 * 1000) {
        groups[groups.length - 1].messages.push(message);
      } else {
        groups.push({
          userId: message.userId,
          username: message.username,
          messages: [message],
          timestamp: message.timestamp
        });
      }
    });

    return groups;
  };

  const messageGroups = groupMessages(messages);

  // Add a test message with reactions for debugging
  const testMessageWithReactions = {
    id: 'test-reaction-msg',
    roomId: roomId || 'test',
    userId: 'test-user',
    username: 'Test User',
    content: 'Test message with reactions (for debugging)',
    type: 'text' as const,
    timestamp: new Date().toISOString(),
    reactions: [
      { emoji: '👍', count: 2, users: ['test-user', 'other-user'] },
      { emoji: '❤️', count: 1, users: ['test-user'] }
    ]
  };
  
  const testGroup = {
    userId: 'test-user',
    username: 'Test User',
    messages: [testMessageWithReactions],
    timestamp: new Date().toISOString()
  };
  
  const messageGroupsWithTest = messageGroups.length > 0 ? messageGroups : [testGroup];

  if (!roomId) {
    return (
      <div className={`card ${height} ${className}`}>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="text-4xl mb-4">💬</div>
            <div className="text-white/70">Join a room to start chatting</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`card ${height} ${className} flex flex-col`}>
      {/* Header */}
      <div className="flex justify-between items-center mb-4 pb-3 border-b border-white/10">
        <div>
          <h3 className="text-lg font-bold">#{roomName}</h3>
          <div className="text-xs text-white/50">
            {isConnected ? 'Connected' : 'Disconnected'}
            {typingUsers.length > 0 && (
              <span className="ml-2 text-blue-400">
                {typingUsers.length === 1 
                  ? `${typingUsers[0]} is typing...`
                  : `${typingUsers.length} people are typing...`
                }
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${
            isConnected ? 'bg-green-400' : 'bg-red-400'
          }`}></div>
          <span className="text-xs text-white/50">
            {messages.length} message{messages.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto space-y-4 pr-2" style={{ maxHeight: 'calc(100% - 120px)' }}>
        {messageGroupsWithTest.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-4xl mb-4">🎉</div>
              <div className="text-white/70 mb-2">Start the conversation!</div>
              <div className="text-white/50 text-sm">Be the first to send a message in this room</div>
            </div>
          </div>
        ) : (
          messageGroupsWithTest.map((group, groupIndex) => {
            const isOwnMessage = group.userId === user?.id;
            
            return (
              <div
                key={`group-${groupIndex}`}
                className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-4`}
              >
                <div className={`max-w-[70%] ${isOwnMessage ? 'order-2' : 'order-1'}`}>
                  {/* Username and timestamp */}
                  <div className={`flex items-center gap-2 mb-1 ${
                    isOwnMessage ? 'justify-end' : 'justify-start'
                  }`}>
                    <span className="text-xs font-medium text-white/70">
                      {isOwnMessage ? 'You' : group.username}
                    </span>
                    <span className="text-xs text-white/40">
                      {formatTime(group.timestamp)}
                    </span>
                  </div>
                  
                  {/* Message bubbles */}
                  <div className="space-y-1">
                    {group.messages.map((message, messageIndex) => (
                      <div
                        key={message.id}
                        className={`relative group ${
                          isOwnMessage ? 'text-right' : 'text-left'
                        }`}
                      >
                        <div
                          className={`inline-block px-4 py-2 rounded-2xl max-w-full break-words ${
                            isOwnMessage
                              ? 'bg-blue-500 text-white rounded-br-md'
                              : 'bg-white/10 text-white rounded-bl-md'
                          } ${
                            messageIndex === 0 ? (isOwnMessage ? 'rounded-tr-2xl' : 'rounded-tl-2xl') : ''
                          } ${
                            messageIndex === group.messages.length - 1 
                              ? (isOwnMessage ? 'rounded-br-md' : 'rounded-bl-md') 
                              : ''
                          }`}
                        >
                          {message.type === 'system' ? (
                            <div className="text-center text-white/60 italic text-sm">
                              {message.content}
                            </div>
                          ) : (
                            <div className="whitespace-pre-wrap">{message.content}</div>
                          )}
                          
                          {/* Message reactions */}
                          {message.reactions && message.reactions.length > 0 && (
                            <div className="flex gap-1 mt-1 justify-end">
                              {message.reactions.map((reaction, index) => (
                                <button
                                  key={index}
                                  onClick={() => {
                                    console.log('Reaction clicked:', reaction.emoji, 'for message:', message.id);
                                    if (reaction.users.includes(user?.id || '')) {
                                      console.log('Removing reaction');
                                      removeReaction(message.id);
                                    } else {
                                      console.log('Adding reaction');
                                      addReaction(message.id, reaction.emoji);
                                    }
                                  }}
                                  className={`px-2 py-1 rounded-full text-xs ${
                                    reaction.users.includes(user?.id || '')
                                      ? 'bg-blue-500/30 text-blue-300'
                                      : 'bg-white/10 text-white/70 hover:bg-white/20'
                                  } transition-colors`}
                                >
                                  {reaction.emoji} {reaction.count}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Quick reactions on hover */}
                        <div className={`absolute top-0 ${
                          isOwnMessage ? 'right-full mr-2' : 'left-full ml-2'
                        } opacity-0 group-hover:opacity-100 transition-opacity`}>
                          <div className="flex gap-1 bg-black/80 rounded-lg px-2 py-1">
                            {['👍', '❤️', '😂', '😮', '😢', '😡'].map(emoji => (
                              <button
                                key={emoji}
                                onClick={() => {
                                  console.log('Adding emoji reaction:', emoji, 'to message:', message.id);
                                  addReaction(message.id, emoji);
                                }}
                                className="hover:bg-white/20 rounded p-1 text-sm transition-colors"
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t border-white/10 pt-4 mt-4">
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <input
              ref={inputRef}
              type="text"
              value={messageText}
              onChange={(e) => handleTyping(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={`Message #${roomName}...`}
              disabled={!isConnected}
              className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-blue-500/50 focus:bg-white/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
          <button
            onClick={handleSendMessage}
            disabled={!messageText.trim() || !isConnected}
            className="bg-blue-500 text-white p-3 rounded-full hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-500"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
            </svg>
          </button>
        </div>
        
        {/* Connection status */}
        {!isConnected && (
          <div className="mt-2 text-xs text-red-400 flex items-center gap-1">
            <div className="w-2 h-2 bg-red-400 rounded-full"></div>
            Disconnected - Messages will be sent when connection is restored
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupChat;